import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

import api from '../../config/axiosInstance';
import ROUTES from '../../routes';

const ContactUsCMS = () => {
    const { code } = useParams();
    const formRef = useRef();

    const [formData, setFormData] = useState({
        section_one_heading: "",
        section_one_sub_heading: "",
        section_two_heading: "",
        section_two_paragraph: "",
        section_two_embeded_map: "",
        section_two_form_title: "",
        section_three_heading: "",
        section_three_sub_heading: "",
        section_three_cta_text: "",
        section_three_cta_link: ""
    });

    const [images, setImages] = useState({
        section_one_background_img: null,
        section_three_background_img: null,
        section_three_background_video: null
    });

    const [imagePreviews, setImagePreviews] = useState({
        section_one_background_img: null,
        section_three_background_img: null,
        section_three_background_video: null
    });

    const [submitting, setSubmitting] = useState(false);
    const [rawMode, setRawMode] = useState({});

    const toggleRawMode = (field) =>
        setRawMode(prev => ({ ...prev, [field]: !prev[field] }));

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e, field) => {
        const file = e.target.files[0];
        if (file) {
            setImages(prev => ({ ...prev, [field]: file }));
            setImagePreviews(prev => ({ ...prev, [field]: URL.createObjectURL(file) }));
        }
    };

    useEffect(() => {
        const $ = window.jQuery;

        const fetchCMS = async () => {
            try {
                const res = await api.get(`/cms/${code}`);
                if (res.status === 200) {
                    const data = res.data.cms;
                    setFormData({
                        section_one_heading: data.section_one_heading || "",
                        section_one_sub_heading: data.section_one_sub_heading || "",
                        section_two_heading: data.section_two_heading || "",
                        section_two_paragraph: data.section_two_paragraph || "",
                        section_two_embeded_map: data.section_two_embeded_map || "",
                        section_two_form_title: data.section_two_form_title || "",
                        section_three_heading: data.section_three_heading || "",
                        section_three_sub_heading: data.section_three_sub_heading || "",
                        section_three_cta_text: data.section_three_cta_text || "",
                        section_three_cta_link: data.section_three_cta_link || "",
                    });

                    setImagePreviews({
                        section_one_background_img: data.section_one_background_img ? `${res.data.base_url}${data.section_one_background_img}` : null,
                        section_three_background_img: data.section_three_background_img ? `${res.data.base_url}${data.section_three_background_img}` : null,
                        section_three_background_video: data.section_three_background_video ? `${res.data.base_url}${data.section_three_background_video}` : null,
                    });
                }
            } catch (err) {
                handleErrors(err);
            }
        };

        fetchCMS();

        $.validator.addMethod('ckeditorRequired', function (value) {
            const textOnly = value.replace(/<[^>]*>/g, '').trim();
            return textOnly.length > 0;
        }, 'This field is required');

        $.validator.addMethod('validEmbedMap', function (value) {
            if (!value || value.trim() === '') return true; // optional field
            const trimmed = value.trim();
            return trimmed.startsWith('<iframe') && trimmed.includes('google.com/maps') && trimmed.endsWith('</iframe>');
        }, 'Please enter a valid Google Maps embed <iframe> code');

        if (formRef.current && $.fn.validate) {
            $(formRef.current).validate({
                rules: {
                    section_one_heading: { required: true },
                    section_two_paragraph: { ckeditorRequired: true },
                    section_two_embeded_map: { validEmbedMap: true },
                },
                ignore: ".ck-editor *",
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }

    }, [code]);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            const data = new FormData();
            Object.entries(formData).forEach(([key, value]) => data.append(key, value));

            Object.entries(images).forEach(([key, file]) => {
                if (file) data.append(key, file);
            });

            try {
                setSubmitting(true);
                const res = await api.put(`/cms/${code}`, data, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                showToast("success", res.data.message, "Success");
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={`/cms/contact_us`}>Contact Us Page</Link>
                    </li>
                </ol>
            </div>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Contact Us Page</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate>
                                <div className="row">
                                    {/* Section 1 */}
                                    <div className="col-12"><h5 className="mb-3">Section One: Hero</h5></div>
                                    {[
                                        { name: 'section_one_heading', label: 'Heading' },
                                        { name: 'section_one_sub_heading', label: 'Sub Heading' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_one_background_img')} />
                                        {imagePreviews.section_one_background_img && (
                                            <img src={imagePreviews.section_one_background_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 2 */}
                                    <div className="col-12"><h5 className="mb-3">Section Two: Get In Touch</h5></div>
                                    {[
                                        { name: 'section_two_heading', label: 'Heading' },
                                        { name: 'section_two_form_title', label: 'Form Title' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-6 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}
                                    <div className="mb-3 col-12">
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <label className="form-label mb-0">Paragraph</label>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => toggleRawMode('section_two_paragraph')}
                                            >
                                                {rawMode['section_two_paragraph'] ? '⬅ Rich Editor' : '</> Raw HTML'}
                                            </button>
                                        </div>
                                        {rawMode['section_two_paragraph'] ? (
                                            <textarea
                                                className="form-control font-monospace"
                                                rows={8}
                                                value={formData.section_two_paragraph}
                                                onChange={e =>
                                                    setFormData(prev => ({ ...prev, section_two_paragraph: e.target.value }))
                                                }
                                            />
                                        ) : (
                                            <CKEditor
                                                editor={ClassicEditor}
                                                config={{
                                                    toolbar: [
                                                        'heading', '|',
                                                        'bold', 'italic', 'link', 'code', 'codeBlock',
                                                        '|', 'bulletedList', 'numberedList'
                                                    ]
                                                }}
                                                data={formData.section_two_paragraph}
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    setFormData(prev => ({ ...prev, section_two_paragraph: data }));
                                                    const hiddenInput = document.querySelector('input[name="section_two_paragraph"]');
                                                    if (hiddenInput) hiddenInput.value = data;
                                                }}
                                            />
                                        )}
                                        <input
                                            type="hidden"
                                            name="section_two_paragraph"
                                            value={formData.section_two_paragraph}
                                        />
                                    </div>

                                    <div className="mb-3 col-12">
                                        <label className="form-label">Embedded Map</label>
                                        <textarea
                                            className="form-control font-monospace"
                                            rows={4}
                                            name="section_two_embeded_map"
                                            placeholder='<iframe src="https://www.google.com/maps/embed?..." ...></iframe>'
                                            value={formData.section_two_embeded_map}
                                            onChange={handleChange}
                                        />
                                        <small className="text-muted">Paste the Google Maps embed &lt;iframe&gt; code here.</small>
                                    </div>

                                    <hr className="my-4" />

                                    {/* Section 3 */}
                                    <div className="col-12"><h5 className="mb-3">Section Three: CTA</h5></div>
                                    {[
                                        { name: 'section_three_heading', label: 'Heading' },
                                        { name: 'section_three_sub_heading', label: 'Sub Heading' },
                                        { name: 'section_three_cta_text', label: 'CTA Text' },
                                        { name: 'section_three_cta_link', label: 'CTA Link' },
                                    ].map(({ name, label }) => (
                                        <div className="mb-3 col-lg-3 col-md-6" key={name}>
                                            <label className="form-label">{label}</label>
                                            <input type="text" className="form-control" name={name} value={formData[name]} onChange={handleChange} />
                                        </div>
                                    ))}
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Image</label>
                                        <input type="file" className="form-control" onChange={(e) => handleImageChange(e, 'section_three_background_img')} />
                                        {imagePreviews.section_three_background_img && (
                                            <img src={imagePreviews.section_three_background_img} alt="Preview" width={200} className="mt-2 rounded" />
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-6">
                                        <label className="form-label">Background Video <small className="text-muted">(.mp4)</small></label>
                                        <input type="file" className="form-control" accept="video/mp4" onChange={(e) => handleImageChange(e, 'section_three_background_video')} />
                                        {imagePreviews.section_three_background_video && (
                                            <video src={imagePreviews.section_three_background_video} width={300} className="mt-2 rounded" controls />
                                        )}
                                    </div>
                                </div>
                                <div className="pt-3">
                                    <button type="button" className="btn btn-sm btn-primary me-2" onClick={handleFormSubmit} disabled={submitting}>
                                        {submitting ? <><i className="spinner-border spinner-border-sm me-1"></i>Saving...</> : <><i className="fa fa-floppy-disk me-2"></i>Save</>}
                                    </button>
                                    <Link to={ROUTES.ROOT} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContactUsCMS;
